import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Building2, Loader2, Save, XCircle } from 'lucide-react';

export const EnterprisePlansTab = ({
  plans, showForm, setShowForm, newPlan, setNewPlan, onCreate, onDeactivate, loading
}) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-[#0B3D91]">Enterprise Plans</h3>
          <p className="text-sm text-slate-500 mt-1">Create custom plans with specific credit limits for enterprise deals</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#0B3D91]"
          data-testid="create-plan-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Plan
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-4" data-testid="create-plan-form">
          <h4 className="font-outfit font-semibold text-slate-900">Create Enterprise Plan</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Plan Name *</label>
              <Input
                value={newPlan.name}
                onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                placeholder="e.g. Enterprise Standard"
                data-testid="plan-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Price (USD) *</label>
              <Input
                type="number"
                min="1"
                value={newPlan.price_monthly}
                onChange={(e) => setNewPlan({ ...newPlan, price_monthly: e.target.value })}
                placeholder="e.g. 5000"
                data-testid="plan-price-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Credit Limit *</label>
              <Input
                type="number"
                min="1"
                value={newPlan.credit_limit}
                onChange={(e) => setNewPlan({ ...newPlan, credit_limit: e.target.value })}
                placeholder="e.g. 500000"
                data-testid="plan-credits-input"
              />
              {newPlan.credit_limit && (
                <p className="text-xs text-slate-400 mt-1">
                  ~{Math.floor(parseInt(newPlan.credit_limit) / 1).toLocaleString()} Gemini analyses or ~{Math.floor(parseInt(newPlan.credit_limit) / 30).toLocaleString()} GPT-4o or ~{Math.floor(parseInt(newPlan.credit_limit) / 46).toLocaleString()} Claude analyses
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <Input
                value={newPlan.description}
                onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                placeholder="Internal notes about this deal"
                data-testid="plan-description-input"
              />
            </div>
          </div>
          {newPlan.price_monthly && newPlan.credit_limit && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-700 font-medium">Margin Preview</p>
              <p className="text-xs text-purple-600 mt-1">
                At ${parseFloat(newPlan.price_monthly).toLocaleString()}/mo with {parseInt(newPlan.credit_limit).toLocaleString()} credits:
                worst-case cost if 100% Claude = ~${(parseInt(newPlan.credit_limit) / 46 * 0.05).toFixed(0)},
                if 100% GPT-4o = ~${(parseInt(newPlan.credit_limit) / 30 * 0.04).toFixed(0)},
                if 100% Gemini = ~${(parseInt(newPlan.credit_limit) * 0.001).toFixed(0)}
              </p>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button
              className="bg-[#0B3D91]"
              onClick={onCreate}
              disabled={loading}
              data-testid="save-plan-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Create Plan
            </Button>
          </div>
        </div>
      )}

      {plans.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No enterprise plans yet</p>
          <p className="text-sm text-slate-400 mt-1">Create your first custom enterprise plan to get started</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {plans.map((plan) => (
            <div
              key={plan.plan_id}
              className={`bg-white rounded-2xl shadow-sm border p-5 transition-all ${plan.active ? 'border-slate-100 hover:border-purple-200' : 'border-red-100 opacity-60'}`}
              data-testid={`enterprise-plan-${plan.plan_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-outfit font-semibold text-slate-900 text-lg">{plan.name}</h4>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${plan.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {plan.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {plan.description && <p className="text-sm text-slate-500 mb-3">{plan.description}</p>}
                  <div className="flex flex-wrap gap-4">
                    <div className="bg-slate-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Monthly Price</p>
                      <p className="font-semibold text-slate-900">${plan.price_monthly?.toLocaleString()}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-purple-500 uppercase tracking-wider">Credit Limit</p>
                      <p className="font-semibold text-purple-700">{plan.credit_limit?.toLocaleString()}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-blue-500 uppercase tracking-wider">Assigned Users</p>
                      <p className="font-semibold text-blue-700">{plan.assigned_users || 0}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Created</p>
                      <p className="text-sm text-slate-700">{plan.created_at ? new Date(plan.created_at).toLocaleDateString() : '-'}</p>
                    </div>
                  </div>
                </div>
                {plan.active && (
                  <button
                    onClick={() => onDeactivate(plan.plan_id, plan.name)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                    title="Deactivate plan"
                    data-testid={`deactivate-plan-${plan.plan_id}`}
                  >
                    <XCircle className="w-5 h-5 text-slate-400 group-hover:text-red-600" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
